import { useInterval } from 'usehooks-ts';
import { useToast, VStack } from '@chakra-ui/react';
import { ReactNode, useMemo, useRef, useState, createContext, useCallback } from 'react';
import { isDefined } from '@duke-hq/lib';

import {
  FileUpload,
  type CreateFilesInput,
  type CreatePartUploadUrlInput,
  type CompleteFileUploadInput,
  type CancelFileUploadInput,
  type AcceptableFilesType,
  PendingFile,
  UPLOAD_CHUNK_SIZE_MB,
  MAX_FAILED_ATTEMPTS,
  UPLOAD_POLL_INTERVAL_MS,
  DeleteFileUploadInput,
  CreateFilesOutput,
  CreatePartUploadUrlOutput,
  type CompleteFileUploadOutput,
  CancelFileUploadOutput,
  DeleteFileUploadOutput,
  MAX_CONCURRENT_UPLOADS,
  MAX_FILE_UPLOAD_SIZE_MB,
  CompleteFileUploadPart,
  FileUploadPart,
  type FileVisibility,
} from '@duke-hq/svc-files-lib/schemas';
import { DropEvent, FileRejection } from 'react-dropzone';
import type { Drn } from '@duke-hq/drn/type';
import { hashMultipartChunk } from '@duke-hq/svc-files-lib/hash';
import { uploadChunkPart } from './uploadChunkPart';
import { uploadFileImperative, type ImperativeUploadResult } from './uploadFileImperative';

export type FileQuantity = 'single' | { max: number } | { min: number; max: number };

function resolveQuantity(quantity: FileQuantity): {
  minFiles: number;
  maxFiles: number;
  allowMultipleFiles: boolean;
} {
  if (quantity === 'single') {
    return { minFiles: 0, maxFiles: 1, allowMultipleFiles: false };
  }
  const min = 'min' in quantity ? quantity.min : 0;
  return { minFiles: min, maxFiles: quantity.max, allowMultipleFiles: quantity.max > 1 };
}

export interface FileUploadActions {
  // Callbacks - these need to be implemented in your API, and a handler
  // must be provided that will call the API.
  createFiles: (input: CreateFilesInput) => Promise<CreateFilesOutput>;
  createPartUploadUrl: (input: CreatePartUploadUrlInput) => Promise<CreatePartUploadUrlOutput>;
  completeFileUpload: (input: CompleteFileUploadInput) => Promise<CompleteFileUploadOutput>;
  cancelFileUpload: (input: CancelFileUploadInput) => Promise<CancelFileUploadOutput>;
  deleteFileUpload: (input: DeleteFileUploadInput) => Promise<DeleteFileUploadOutput>;
}

export type { ImperativeUploadResult } from './uploadFileImperative';

export interface FileUploadContextType {
  files: FileUpload[];
  handleRemoveFile: (fileDrn: Drn, uploadId: string) => Promise<void>;
  maxFiles?: number;
  allowMultipleFiles: boolean;
  minSizePerFileKB: number;
  maxSizePerFileMB: number;
  hasRequiredFileUploads: boolean;
  hasPendingFileUploads: boolean;
  uploadProgress?: number;
  errors: string[];
  defaultAttachedToId?: string;
  defaultAttachedToKind?: string;
  uploadFile: (file: File) => Promise<ImperativeUploadResult>;
  isUploading: boolean;

  // -- FileUploadDropzone -----------------------------
  handleFileDropzoneChange: <T extends File>(
    acceptedFiles: T[],
    fileRejections: FileRejection[],
    event: DropEvent,
    attachedToKind?: string,
    attachedToId?: string,
  ) => void;
  // -- FileUploadTrigger ------------------------------
  handleFileInputChange: (
    e: React.ChangeEvent<HTMLInputElement>,
    attachedToKind?: string,
    attachedToId?: string,
  ) => void;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  accept?: AcceptableFilesType;
  // comma separated string of file extensions
  acceptExtensions: string;
}

export const FileUploadContext = createContext<FileUploadContextType | undefined>(undefined);

export function FileUploadContextProvider({
  children,
  quantity = { max: 50 },
  accept,
  minSizePerFileKB = 1, //1KB
  maxSizePerFileMB = MAX_FILE_UPLOAD_SIZE_MB,
  actions,
  attachedToId,
  attachedToKind,
  visibility,
  onFileComplete,
}: {
  children: ReactNode;
  quantity?: FileQuantity;
  accept?: Record<string, string[]>;
  minSizePerFileKB?: number;
  maxSizePerFileMB?: number;
  actions: FileUploadActions;
  attachedToId?: string;
  attachedToKind?: string;
  visibility?: FileVisibility;
  onFileComplete?: (output: CompleteFileUploadOutput) => void;
}) {
  const { minFiles, maxFiles, allowMultipleFiles } = resolveQuantity(quantity);

  const value = useFiles({
    minFiles,
    maxFiles,
    accept,
    allowMultipleFiles,
    minSizePerFileKB,
    maxSizePerFileMB,
    actions,
    attachedToId,
    attachedToKind,
    visibility,
    onFileComplete,
  });

  return (
    <FileUploadContext.Provider value={value}>
      <VStack align="stretch" spacing={2} width="100%" height="100%">
        {children}
      </VStack>
    </FileUploadContext.Provider>
  );
}

function useFiles({
  minFiles,
  maxFiles,
  allowMultipleFiles,
  accept,
  minSizePerFileKB,
  maxSizePerFileMB,
  actions,
  attachedToId,
  attachedToKind,
  visibility,
  onFileComplete,
}: {
  minFiles: number;
  maxFiles: number;
  allowMultipleFiles: boolean;
  accept?: AcceptableFilesType;
  minSizePerFileKB: number;
  maxSizePerFileMB: number;
  actions: FileUploadActions;
  attachedToId?: string;
  attachedToKind?: string;
  visibility?: FileVisibility;
  onFileComplete?: (output: CompleteFileUploadOutput) => void;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const toast = useToast();

  const acceptExtensions = useMemo(() => {
    if (!accept) {
      return '';
    }
    if (typeof accept === 'string') {
      return accept;
    }
    return Object.values(accept).join(',');
  }, [accept]);

  const [errors, setErrors] = useState<string[]>([]);

  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);

  const hasPendingFileUploads = useMemo(() => {
    const files = pendingFiles.filter((f) => f.failedAttempts <= MAX_FAILED_ATTEMPTS);

    return files.some((file) => file.status === 'pending' || file.status === 'confirmingUpload');
  }, [pendingFiles]);

  const [abortControllers, setAbortControllers] = useState<Record<string, AbortController>>({});

  const [activeImperativeUploads, setActiveImperativeUploads] = useState(0);

  const updateFile = useCallback(
    (fileDrn: string, uploadId: string, updater: (file: PendingFile) => PendingFile) => {
      setPendingFiles((prev) =>
        prev.map((f) => {
          if (f.drn !== fileDrn) {
            return f;
          }
          if (f.uploadId !== uploadId) {
            return f;
          }
          return updater(f);
        }),
      );
    },
    [],
  );

  const updateFilePart = useCallback(
    (
      fileDrn: string,
      uploadId: string,
      partNumber: number,
      updater: (part: FileUploadPart) => FileUploadPart,
    ) => {
      updateFile(fileDrn, uploadId, (file) => ({
        ...file,
        parts: file.parts.map((p) => {
          if (p.partNumber !== partNumber) {
            return p;
          }
          return updater(p);
        }),
      }));
    },
    [updateFile],
  );

  // -- Handlers ---------------------------------------------------------------

  const handleFileChange = useCallback(
    async (files: File[], attachedToKind?: string, attachedToId?: string) => {
      try {
        const result = await actions.createFiles({
          attachedToKind,
          attachedToId,
          visibility,
          uploadFiles: files.map((file) => ({
            name: file.name,
            mimetype: file.type,
            sizeInBytes: file.size,
          })),
        });

        setPendingFiles((prev) => [
          ...prev,
          ...(result.files
            .map((f) => {
              const file = files.find((file) => file.name === f.name);
              if (!file) {
                return;
              }

              return {
                id: f.id,
                name: f.name,
                drn: f.drn,
                uploadId: f.uploadId,
                sizeInBytes: f.sizeInBytes,
                mimetype: f.mimetype,
                parts: Array.from(
                  {
                    length: Math.ceil(f.sizeInBytes / UPLOAD_CHUNK_SIZE_MB),
                  },
                  (_, i) => ({
                    partNumber: i + 1,
                    etag: undefined,
                    progress: 0,
                  }),
                ),
                file,
                previewUrl: URL.createObjectURL(file),
                failedAttempts: 0,
                status: 'pending',
                attachedToId,
                attachedToKind,
              } satisfies PendingFile;
            })
            .filter(isDefined) satisfies PendingFile[]),
        ]);
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        setErrors((prev) => [...prev, message]);
        toast({
          status: 'error',
          title: `Upload failed`,
          description: message,
          duration: 5000,
          isClosable: true,
        });
      }
    },
    [actions.createFiles, setPendingFiles, toast, visibility],
  );

  const uploadFile = useCallback(
    async (file: File): Promise<ImperativeUploadResult> => {
      setActiveImperativeUploads((c) => c + 1);
      try {
        return await uploadFileImperative({
          file,
          actions,
          attachedToKind,
          attachedToId,
          visibility,
        });
      } finally {
        setActiveImperativeUploads((c) => c - 1);
      }
    },
    [
      actions.createFiles,
      actions.createPartUploadUrl,
      actions.completeFileUpload,
      actions.cancelFileUpload,
      attachedToKind,
      attachedToId,
      visibility,
    ],
  );

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, attachedToKind?: string, attachedToId?: string) => {
      void handleFileChange(Array.from(e.target.files || []), attachedToKind, attachedToId);
      e.target.value = ''; // Reset after handling change
    },
    [handleFileChange],
  );

  const handleFileDropzoneChange = useCallback(
    <T extends File>(
      acceptedFiles: T[],
      fileRejections: FileRejection[],
      _: DropEvent,
      attachedToKind?: string,
      attachedToId?: string,
    ) => {
      if (acceptedFiles.length > 0) {
        void handleFileChange(acceptedFiles, attachedToKind, attachedToId);
      }

      if (fileRejections.length > 0) {
        let errorMessage = '';
        fileRejections.forEach((rejection) => {
          rejection.errors.forEach((error) => {
            switch (error.code) {
              case 'too-many-files':
                errorMessage = `Too many files. Max allowed is` + ' ' + maxFiles + '.';
                break;

              case 'file-too-large':
                errorMessage = `File is too large. Max size is` + ' ' + maxSizePerFileMB + 'MB.';
                break;

              case 'file-too-small':
                errorMessage = `File is too small. Min size is ${minSizePerFileKB}KB.`;
                break;

              case 'file-invalid-type':
                errorMessage =
                  accept?.length != null
                    ? `Invalid file type. Accepted types are:` + ' ' + acceptExtensions
                    : `Invalid file type.`;
                break;

              default:
                errorMessage = `An error occurred while processing the file.`;
            }
          });
        });

        toast({
          title: `File Upload Error`,
          description: errorMessage,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    },
    [
      handleFileChange,
      toast,
      accept,
      acceptExtensions,
      maxFiles,
      maxSizePerFileMB,
      minSizePerFileKB,
    ],
  );

  const handleRemoveFile = useCallback(
    async (fileDrn: Drn, uploadId: string) => {
      try {
        const file = pendingFiles.find((f) => f.drn === fileDrn);

        if (file == null) {
          return;
        }

        // abort any ongoing uploads
        const controllersToRemove = Object.entries(abortControllers)
          .filter(([key]) => key.startsWith(file.id))
          .map(([key]) => key);

        for (const key of controllersToRemove) {
          if (abortControllers[key]) {
            abortControllers[key].abort();
          }
        }

        // remove abort controllers
        setAbortControllers((prev) => {
          return Object.fromEntries(
            Object.entries(prev).filter(([key]) => !controllersToRemove.includes(key)),
          );
        });

        if (file?.status === 'complete') {
          await actions.deleteFileUpload({
            fileDrn,
            uploadId,
          });
          // return;
        } else {
          await actions.cancelFileUpload({
            fileDrn: file.drn,
            uploadId: file.uploadId,
          });
        }

        // remove file from pending
        setPendingFiles((prev) => prev.filter((f) => f.drn !== fileDrn));
      } catch (err) {
        toast({
          status: 'error',
          title: err instanceof Error ? err.message : 'Error removing file',
          description: 'Please try again or contact support if the issue persists.',
        });
      }
    },
    [abortControllers, setPendingFiles, actions.cancelFileUpload, actions.deleteFileUpload],
  );

  const uploadFilePart = useCallback(
    async (file: PendingFile, partNumber: number) => {
      if (file.failedAttempts >= MAX_FAILED_ATTEMPTS) {
        await actions
          .cancelFileUpload({ fileDrn: file.drn, uploadId: file.uploadId })
          .catch(() => {});
        updateFile(file.drn, file.uploadId, (f) => ({ ...f, status: 'failed' }));
        return;
      }
      const controller = new AbortController();

      const signal = controller.signal;

      const abortControllerId = file.uploadId + '_' + partNumber;

      setAbortControllers((prev) => ({
        ...prev,
        [abortControllerId]: controller,
      }));

      const removeController = () => {
        setAbortControllers((prev) => {
          const { [abortControllerId]: _, ...rest } = prev;
          return rest;
        });
      };

      const start = (partNumber - 1) * UPLOAD_CHUNK_SIZE_MB;

      const end = Math.min(start + UPLOAD_CHUNK_SIZE_MB, file.file.size);

      const chunk = file.file.slice(start, end);

      const checksumSHA256 = await hashMultipartChunk(chunk);

      const url = await actions.createPartUploadUrl({
        fileDrn: file.drn,
        uploadId: file.uploadId,
        partNumber: partNumber,
        checksumSHA256,
      });

      updateFilePart(file.drn, file.uploadId, partNumber, (part) => ({
        ...part,
        uploadUrl: url,
        checksumSHA256,
      }));

      const handleErr = () => {
        controller.abort();

        removeController();

        updateFilePart(file.drn, file.uploadId, partNumber, (p) => ({
          ...p,
          uploadUrl: undefined,
          checksumSHA256: undefined,
          progress: 0,
        }));

        updateFile(file.drn, file.uploadId, (f) => ({
          ...f,
          failedAttempts: f.failedAttempts + 1,
        }));
      };

      try {
        const { etag } = await uploadChunkPart({
          chunk,
          uploadUrl: url,
          checksumSHA256,
          abortSignal: signal,
          onProgress: (progress) => {
            updateFilePart(file.drn, file.uploadId, partNumber, (p) => ({
              ...p,
              progress,
            }));
          },
        });

        updateFilePart(file.drn, file.uploadId, partNumber, (p) => ({
          ...p,
          uploadUrl: url,
          checksumSHA256,
          progress: 100,
          etag,
        }));

        removeController();
      } catch {
        handleErr();
      }
    },
    [
      actions.createPartUploadUrl,
      actions.cancelFileUpload,
      updateFile,
      updateFilePart,
      setPendingFiles,
    ],
  );

  // Each interval we check if we can upload more parts
  useInterval(
    () => {
      // Check if we can upload more parts
      {
        if (!hasPendingFileUploads) {
          return;
        }

        const currentlyUploading = Object.keys(abortControllers).length;

        if (currentlyUploading >= MAX_CONCURRENT_UPLOADS) {
          return;
        }

        const availableSlots = MAX_CONCURRENT_UPLOADS - currentlyUploading;

        const nextParts: { fileToUpload: PendingFile; partNumber: number }[] = [];

        // First, find files that need uploading (up to available slots)
        const filesNeedingUpload = pendingFiles
          .filter((file) =>
            file.parts.some((part) => part.etag === undefined && part.uploadUrl === undefined),
          )
          .slice(0, availableSlots);

        let remainingSlots = availableSlots;

        // First pass: one part per file
        filesNeedingUpload.forEach((file) => {
          const partToUpload = file.parts.find((part) => part.uploadUrl === undefined);

          if (partToUpload) {
            nextParts.push({
              fileToUpload: file,
              partNumber: partToUpload.partNumber,
            });

            remainingSlots--;
          }
        });

        // Second pass: use remaining slots for additional parts from the same files
        if (remainingSlots > 0) {
          for (const file of filesNeedingUpload) {
            const additionalParts = file.parts.filter((part) => part.uploadUrl != null).slice(1); // Skip first part since it was handled in first pass

            for (const part of additionalParts) {
              if (remainingSlots <= 0) {
                break;
              }

              nextParts.push({
                fileToUpload: file,
                partNumber: part.partNumber,
              });

              remainingSlots--;
            }

            if (remainingSlots <= 0) {
              break;
            }
          }
        }

        void nextParts.map(async ({ fileToUpload, partNumber }) =>
          uploadFilePart(fileToUpload, partNumber),
        );
      }
      // now check for any files with all parts uploaded, that needs to be marked as complete
      {
        // mark files complete
        const filesToComplete = pendingFiles.filter(
          (file) => file.status === 'pending' && file.parts.every((part) => part?.etag != null),
        );

        // On each pass, we only attempt to complete one file
        //
        // If we try to do more, we can hit browser and server limits, causing
        // the requests to fail in unexpected and silent ways.
        const file = filesToComplete[0];
        if (
          !file ||
          file.status === 'confirmingUpload' ||
          file.failedAttempts >= MAX_FAILED_ATTEMPTS
        ) {
          return;
        }

        updateFile(file.drn, file.uploadId, (f) => ({
          ...f,
          status: 'confirmingUpload',
        }));

        // Check that we have all the parts, and each part is valid
        const partsChecked = file.parts.map((part) => CompleteFileUploadPart.safeParse(part));

        const uploadIsComplete = partsChecked.every((part) => part.success);

        if (!uploadIsComplete) {
          return;
        }

        const parts = partsChecked.map((part) => part.data);

        actions
          .completeFileUpload({
            fileDrn: file.drn,
            uploadId: file.uploadId,
            parts,
          })
          .then((result) => {
            updateFile(file.drn, file.uploadId, (f) => ({
              ...f,
              status: 'complete',
            }));

            onFileComplete?.(result);
          })
          .catch(() => {
            updateFile(file.drn, file.uploadId, (f) => ({
              ...f,
              status: f.failedAttempts >= MAX_FAILED_ATTEMPTS ? 'failed' : 'pending',
              failedAttempts: f.failedAttempts + 1,
            }));
          });
      }
    },
    // only run the interval if we have pending files
    hasPendingFileUploads ? UPLOAD_POLL_INTERVAL_MS : null,
  );

  const { files, hasRequiredFileUploads } = useMemo(() => {
    const files: FileUpload[] = pendingFiles.map((file) => ({
      id: file.id,
      name: file.name,
      type: file.mimetype ?? '',
      sizeInBytes: file.sizeInBytes ?? 0,
      previewUrl: file.previewUrl,
      progress: file.parts.reduce((acc, part) => acc + (part.progress ?? 0), 0) / file.parts.length,
      status: file.status,
      failed: file.failedAttempts >= MAX_FAILED_ATTEMPTS,
      remove: () => handleRemoveFile(file.drn, file.uploadId),
    }));

    return {
      files,
      hasRequiredFileUploads: files.filter((file) => file.status === 'complete').length >= minFiles,
    };
  }, [pendingFiles, minFiles, handleRemoveFile]);

  const uploadProgress = useMemo(() => {
    if (!hasPendingFileUploads) {
      return undefined;
    }

    const pendingUploads = pendingFiles.filter((file) => file.status === 'pending');

    const totalProgress = pendingUploads.reduce((acc, file) => {
      const fileProgress =
        file.parts.reduce((partAcc, part) => partAcc + (part.progress ?? 0), 0) / file.parts.length;
      return acc + fileProgress;
    }, 0);

    return totalProgress / pendingUploads.length;
  }, [pendingFiles, hasPendingFileUploads]);

  return {
    accept,
    acceptExtensions,

    fileInputRef,
    maxFiles,
    allowMultipleFiles,
    minSizePerFileKB,
    maxSizePerFileMB,
    errors,
    files,
    uploadProgress,
    hasRequiredFileUploads,
    hasPendingFileUploads,
    defaultAttachedToId: attachedToId,
    defaultAttachedToKind: attachedToKind,

    handleRemoveFile,
    handleFileDropzoneChange,
    handleFileInputChange,
    uploadFile,
    isUploading: activeImperativeUploads > 0,
  } satisfies FileUploadContextType;
}
